<div
  #root
  class="relative h-full w-full overflow-hidden rounded-xl bg-gray-800 text-white">
  @if (phase() === 'preview') {
    <!-- Pre-join device check -->
    <div class="flex h-full flex-col">
      <div
        #previewContainer
        class="relative min-h-0 grow bg-black [&>*]:h-full [&>*]:w-full">
        @if (!localView()) {
          <div
            class="absolute inset-0 flex items-center justify-center text-sm text-white/70">
            Camera is off
          </div>
        }
      </div>

      <div class="space-y-3 p-4">
        @if (errorMessage(); as err) {
          <p class="text-sm text-red-400" role="alert">{{ err }}</p>
        }
        <ng-container [ngTemplateOutlet]="deviceControls" />

        <div class="flex items-center gap-3 pt-1">
          <button
            type="button"
            [class]="micButtonClasses()"
            [attr.aria-label]="
              micMuted() ? 'Unmute microphone' : 'Mute microphone'
            "
            (click)="toggleMute.emit()">
            <mat-icon svgIcon="telehealth-microphone"></mat-icon>
          </button>
          <button
            type="button"
            [class]="cameraButtonClasses()"
            [attr.aria-label]="
              cameraOn() ? 'Turn camera off' : 'Turn camera on'
            "
            (click)="toggleCamera.emit()">
            <mat-icon svgIcon="telehealth-camera"></mat-icon>
          </button>
          <icaredx-button
            class="ml-auto"
            color="success"
            svgIcon="phone"
            (clicked)="join.emit()">
            {{ primaryActionLabel() }}
          </icaredx-button>
        </div>
      </div>
    </div>
  } @else if (phase() === 'waiting') {
    <!-- Waiting for the meeting to start -->
    <div
      class="flex h-full flex-col items-center justify-center gap-4 bg-white px-6 py-10 text-center text-gray-900">
      <mat-icon
        class="!h-[90px] !w-[90px]"
        svgIcon="telehealth-waiting"></mat-icon>

      <div class="space-y-1">
        @if (title(); as callTitle) {
          <h3 class="text-xl font-bold text-gray-900">{{ callTitle }}</h3>
        }
        @if (scheduleLabel(); as schedule) {
          <p
            class="flex items-center justify-center gap-1.5 text-sm text-gray-500">
            <mat-icon class="!h-4 !w-4" svgIcon="clock-outline"></mat-icon>
            {{ schedule }}
          </p>
        }
      </div>

      @if (waitingErrored()) {
        <div class="space-y-1">
          <p class="font-semibold text-gray-900">
            We couldn’t connect you to the meeting
          </p>
          <p class="text-sm text-gray-500" role="alert">
            {{ waitingErrorMessage() }}
          </p>
        </div>

        <div class="flex items-center gap-3">
          <icaredx-button (clicked)="rejoin.emit()">Try again</icaredx-button>
          <button
            type="button"
            class="rounded-lg border border-gray-200 px-6 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
            (click)="hangUp.emit()">
            Cancel
          </button>
        </div>
      } @else {
        <icaredx-spinner size="large" />

        <div class="space-y-1">
          @if (state() === 'connecting') {
            <p class="font-semibold text-gray-900">
              Connecting you to the meeting…
            </p>
            <p class="text-sm text-gray-500">It can take up to a few minutes</p>
          } @else {
            <p class="font-semibold text-gray-900">
              Waiting for the other participant to join…
            </p>
            <p class="text-sm text-gray-500">
              The meeting will start once everyone has joined
            </p>
          }
        </div>

        <button
          type="button"
          class="rounded-lg border border-gray-200 px-6 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50"
          (click)="hangUp.emit()">
          Cancel
        </button>
      }
    </div>
  } @else {
    <!-- Live call -->
    <div
      #remoteContainer
      class="absolute inset-0 [&>*]:h-full [&>*]:w-full"></div>

    @if (showEnded()) {
      <div
        class="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-900/90 px-6 text-center"
        role="alert">
        <p class="text-sm text-white/80">{{ endedMessage() }}</p>
        <div class="flex items-center gap-2">
          <icaredx-button (clicked)="rejoin.emit()">Rejoin</icaredx-button>
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm text-white/70 hover:text-white"
            (click)="hangUp.emit()">
            Close
          </button>
        </div>
      </div>
    } @else if (showReconnecting()) {
      <div
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-900/85 px-6 text-center">
        <div
          class="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white"
          aria-hidden="true"></div>
        <p class="text-sm text-white/80">Reconnecting…</p>
      </div>
    } @else if (showConnecting()) {
      <div
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-900/85 px-6 text-center">
        <div
          class="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white"
          aria-hidden="true"></div>
        <p class="text-sm text-white/80">Connecting…</p>
      </div>
    } @else if (showWaiting()) {
      <div
        class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-900/85 px-6 text-center">
        <p class="text-sm text-white/80">
          {{ waitingMessage() }}
        </p>
      </div>
    } @else if (showRemoteName()) {
      <div
        class="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
        <span
          class="max-w-full truncate text-base font-medium text-white/90">
          {{ remoteDisplayName() }}
        </span>
        <span class="text-xs text-white/60">Camera is off</span>
      </div>
    }

    <!-- Self-view thumbnail -->
    @if (showSelfView()) {
      <div [class]="selfViewClasses()">
        <div
          #selfContainer
          class="absolute inset-0 [&>*]:h-full [&>*]:w-full [&_video]:!h-full [&_video]:!w-full [&_video]:!object-cover"></div>
        @if (showSelfName()) {
          <div
            class="absolute inset-0 flex items-center justify-center px-2 text-center">
            <span class="max-w-full truncate text-xs font-medium text-white/90">
              {{ localDisplayName() }}
            </span>
          </div>
        }
      </div>
    }

    <!-- Device settings popover -->
    @if (settingsOpen()) {
      <div
        class="absolute bottom-20 left-1/2 w-72 -translate-x-1/2 space-y-3 rounded-lg bg-gray-800 p-4 shadow-xl ring-1 ring-white/10">
        <ng-container [ngTemplateOutlet]="deviceControls" />
      </div>
    }

    <!-- Control strip: timing + status above, controls below -->
    <div class="absolute inset-x-0 bottom-0 flex flex-col gap-2 px-4 pb-4 pt-8">
      @if (weakConnection() || endingSoon()) {
        <span
          class="self-start rounded-full bg-amber-500/90 px-2 py-0.5 text-[11px] text-white">
          {{ weakConnection() ? 'Weak connection' : 'This visit ends soon' }}
        </span>
      }

      <div class="flex items-center justify-between text-xs text-white/90">
        <span>{{ elapsedLabel() }}</span>
        @if (remainingLabel(); as remaining) {
          <span [class]="endsAtClasses()">{{ remaining }}</span>
        }
      </div>

      <div class="flex items-center gap-2">
        @if (canResize()) {
          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
            [disabled]="displayMode() === 'mini' && !canExpand()"
            [attr.aria-label]="
              displayMode() === 'mini' ? 'Expand to side panel' : 'Minimize'
            "
            (click)="toggleSize.emit()">
            <mat-icon
              [svgIcon]="
                displayMode() === 'mini'
                  ? 'telehealth-to-side'
                  : 'telehealth-to-mini'
              "></mat-icon>
          </button>
        }

        <div class="mx-auto flex items-center gap-2">
          <button
            type="button"
            [class]="cameraButtonClasses()"
            [attr.aria-label]="
              cameraOn() ? 'Turn camera off' : 'Turn camera on'
            "
            (click)="toggleCamera.emit()">
            <mat-icon svgIcon="telehealth-camera"></mat-icon>
          </button>

          <button
            type="button"
            [class]="micButtonClasses()"
            [attr.aria-label]="
              micMuted() ? 'Unmute microphone' : 'Mute microphone'
            "
            (click)="toggleMute.emit()">
            <mat-icon svgIcon="telehealth-microphone"></mat-icon>
          </button>

          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
            [attr.aria-label]="
              isFullscreen() ? 'Exit fullscreen' : 'Fullscreen'
            "
            (click)="toggleFullscreen()">
            <mat-icon
              [svgIcon]="
                isFullscreen() ? 'telehealth-collapse' : 'telehealth-expand'
              "></mat-icon>
          </button>

          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
            aria-label="Device settings"
            [attr.aria-expanded]="settingsOpen()"
            (click)="toggleSettings()">
            <mat-icon svgIcon="telehealth-settings"></mat-icon>
          </button>

          @if (screenShareSupported()) {
            <button
              type="button"
              [class]="screenShareButtonClasses()"
              [attr.aria-label]="
                screenSharing() ? 'Stop sharing screen' : 'Share screen'
              "
              (click)="toggleScreenShare.emit()">
              <mat-icon svgIcon="telehealth-screen-share"></mat-icon>
            </button>
          }
        </div>

        <icaredx-button color="danger" (clicked)="hangUp.emit()">
          <span class="inline-flex items-center gap-x-2">
            <mat-icon class="!h-4 !w-4" svgIcon="phone-off"></mat-icon>
            End Call
          </span>
        </icaredx-button>
      </div>
    </div>
  }
</div>

<!-- Shared camera / microphone / speaker pickers -->
<ng-template #deviceControls>
  @if (cameras().length > 0) {
    <label class="block text-xs text-white/70">
      Camera
      <select
        class="mt-1 w-full rounded bg-white/10 px-2 py-1 text-sm text-white"
        (change)="onCameraChange($event)">
        @for (camera of cameras(); track camera.id) {
          <option
            [value]="camera.id"
            [selected]="camera.id === selectedCameraId()">
            {{ camera.name }}
          </option>
        }
      </select>
    </label>
  }

  @if (microphones().length > 0) {
    <label class="block text-xs text-white/70">
      Microphone
      <select
        class="mt-1 w-full rounded bg-white/10 px-2 py-1 text-sm text-white"
        (change)="onMicrophoneChange($event)">
        @for (microphone of microphones(); track microphone.id) {
          <option
            [value]="microphone.id"
            [selected]="microphone.id === selectedMicrophoneId()">
            {{ microphone.name }}
          </option>
        }
      </select>
    </label>
  }

  @if (speakers().length > 0) {
    <label class="block text-xs text-white/70">
      Speaker
      <select
        class="mt-1 w-full rounded bg-white/10 px-2 py-1 text-sm text-white"
        (change)="onSpeakerChange($event)">
        @for (speaker of speakers(); track speaker.id) {
          <option
            [value]="speaker.id"
            [selected]="speaker.id === selectedSpeakerId()">
            {{ speaker.name }}
          </option>
        }
      </select>
    </label>
  }
</ng-template>
